// CANONICAL COPY (GH #7115). src/constants/obligations/index.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

const { buildControlIndex } = require('../controlIndex.cjs');

// ── Cadence helpers ─────────────────────────────────────────────────────────

const CADENCE_OPTIONS = [
  { value: '1m', label: 'Monthly' },
  { value: '3m', label: 'Quarterly' },
  { value: '6m', label: 'Semi-annually' },
  { value: '1y', label: 'Annually' },
  { value: 'once', label: 'One-time' },
];

/** Convert cadence frequency code to milliseconds for due-date calculation */
function cadenceToMs(freq) {
  const DAY = 24 * 60 * 60 * 1000;
  switch (freq) {
    case '1m':
      return 30 * DAY;
    case '3m':
      return 91 * DAY;
    case '6m':
      return 182 * DAY;
    case '1y':
      return 365 * DAY;
    case 'once':
      return null;
    default:
      return 365 * DAY;
  }
}

// ── Categories ──────────────────────────────────────────────────────────────

const OBLIGATION_CATEGORIES = [
  { value: 'governance', label: 'Governance & Policy' },
  { value: 'assets', label: 'Asset Management' },
  { value: 'access', label: 'Access Control' },
  { value: 'people', label: 'People & HR' },
  { value: 'vendors', label: 'Vendor Management' },
  { value: 'incidents', label: 'Incident Management' },
  { value: 'continuity', label: 'Business Continuity' },
  { value: 'compliance', label: 'Legal & Compliance' },
  { value: 'risk', label: 'Risk Management' },
  { value: 'technical', label: 'Technical Controls' },
];

// ── Obligation shard imports ─────────────────────────────────────────────────

const obl_policy_defined = require('./obl-policy-defined.cjs');
const obl_policy_review = require('./obl-policy-review.cjs');
const obl_roles_responsibilities = require('./obl-roles-responsibilities.cjs');
const obl_segregation_duties = require('./obl-segregation-duties.cjs');
const obl_management_commitment = require('./obl-management-commitment.cjs');
const obl_management_body_accountability = require('./obl-management-body-accountability.cjs');
const obl_authority_contacts = require('./obl-authority-contacts.cjs');
const obl_threat_intelligence_review = require('./obl-threat-intelligence-review.cjs');
const obl_asset_inventory = require('./obl-asset-inventory.cjs');
const obl_acceptable_use = require('./obl-acceptable-use.cjs');
const obl_asset_return = require('./obl-asset-return.cjs');
const obl_data_classification = require('./obl-data-classification.cjs');
const obl_access_control_policy = require('./obl-access-control-policy.cjs');
const obl_privileged_access_management = require('./obl-privileged-access-management.cjs');
const obl_access_rights_review = require('./obl-access-rights-review.cjs');
const obl_vendor_policy = require('./obl-vendor-policy.cjs');
const obl_vendor_assessment = require('./obl-vendor-assessment.cjs');
const obl_vendor_contracts = require('./obl-vendor-contracts.cjs');
const obl_physical_security = require('./obl-physical-security.cjs');
const obl_physical_monitoring = require('./obl-physical-monitoring.cjs');
const obl_incident_response_plan = require('./obl-incident-response-plan.cjs');
const obl_incident_response_exercise = require('./obl-incident-response-exercise.cjs');
const obl_incident_evidence = require('./obl-incident-evidence.cjs');
const obl_incident_learning = require('./obl-incident-learning.cjs');
const obl_regulatory_incident_notification = require('./obl-regulatory-incident-notification.cjs');
const obl_incident_communication = require('./obl-incident-communication.cjs');
const obl_bcp_plan = require('./obl-bcp-plan.cjs');
const obl_business_impact_analysis = require('./obl-business-impact-analysis.cjs');
const obl_bc_exercise = require('./obl-bc-exercise.cjs');
const obl_backup_test = require('./obl-backup-test.cjs');
const obl_legal_register = require('./obl-legal-register.cjs');
const obl_compliance_review = require('./obl-compliance-review.cjs');
const obl_independent_review = require('./obl-independent-review.cjs');
const obl_security_event_reporting = require('./obl-security-event-reporting.cjs');
const obl_employee_screening = require('./obl-employee-screening.cjs');
const obl_employment_terms = require('./obl-employment-terms.cjs');
const obl_security_awareness = require('./obl-security-awareness.cjs');
const obl_training_deadline_compliance = require('./obl-training-deadline-compliance.cjs');
const obl_disciplinary_process = require('./obl-disciplinary-process.cjs');
const obl_nda_signed = require('./obl-nda-signed.cjs');
const obl_offboarding_access = require('./obl-offboarding-access.cjs');
const obl_risk_assessment = require('./obl-risk-assessment.cjs');
const obl_risk_treatment = require('./obl-risk-treatment.cjs');
const obl_security_objectives = require('./obl-security-objectives.cjs');
const obl_isms_context_scope = require('./obl-isms-context-scope.cjs');
const obl_isms_resources = require('./obl-isms-resources.cjs');
const obl_isms_communication_awareness = require('./obl-isms-communication-awareness.cjs');
const obl_operating_procedures = require('./obl-operating-procedures.cjs');
const obl_operational_planning = require('./obl-operational-planning.cjs');
const obl_continual_improvement = require('./obl-continual-improvement.cjs');
const obl_management_review = require('./obl-management-review.cjs');
const obl_isms_metrics = require('./obl-isms-metrics.cjs');
const obl_internal_audit = require('./obl-internal-audit.cjs');
const obl_vulnerability_scan = require('./obl-vulnerability-scan.cjs');
const obl_patch_management = require('./obl-patch-management.cjs');
const obl_software_installation = require('./obl-software-installation.cjs');
const obl_infrastructure_redundancy = require('./obl-infrastructure-redundancy.cjs');
const obl_security_monitoring = require('./obl-security-monitoring.cjs');
const obl_logging_review = require('./obl-logging-review.cjs');
const obl_network_security_review = require('./obl-network-security-review.cjs');
const obl_sdlc_policy = require('./obl-sdlc-policy.cjs');
const obl_security_testing = require('./obl-security-testing.cjs');
const obl_pentest_external = require('./obl-pentest-external.cjs');
const obl_change_management = require('./obl-change-management.cjs');
const obl_cryptography_policy = require('./obl-cryptography-policy.cjs');
const obl_records_protection = require('./obl-records-protection.cjs');
const obl_data_deletion = require('./obl-data-deletion.cjs');
const obl_secure_authentication = require('./obl-secure-authentication.cjs');
const obl_remote_working_policy = require('./obl-remote-working-policy.cjs');
const obl_privacy_pii = require('./obl-privacy-pii.cjs');
const obl_lawful_basis = require('./obl-lawful-basis.cjs');
const obl_consent = require('./obl-consent.cjs');
const obl_data_subject_rights = require('./obl-data-subject-rights.cjs');
const obl_data_portability = require('./obl-data-portability.cjs');
const obl_transparency_notices = require('./obl-transparency-notices.cjs');
const obl_records_of_processing = require('./obl-records-of-processing.cjs');
const obl_dpia = require('./obl-dpia.cjs');
const obl_dpo = require('./obl-dpo.cjs');
const obl_international_transfer = require('./obl-international-transfer.cjs');
const obl_privacy_by_design = require('./obl-privacy-by-design.cjs');
const obl_malware_protection = require('./obl-malware-protection.cjs');
const obl_email_security = require('./obl-email-security.cjs');
const obl_config_management = require('./obl-config-management.cjs');
const obl_web_filtering = require('./obl-web-filtering.cjs');
const obl_payment_application_security = require('./obl-payment-application-security.cjs');
const obl_storage_media_management = require('./obl-storage-media-management.cjs');
const obl_vulnerability_scan_programme = require('./obl-vulnerability-scan-programme.cjs');
const obl_cde_scoping = require('./obl-cde-scoping.cjs');
const obl_poi_tamper_protection = require('./obl-poi-tamper-protection.cjs');
const obl_ip_rights = require('./obl-ip-rights.cjs');
const obl_equipment_disposal = require('./obl-equipment-disposal.cjs');
const obl_information_transfer = require('./obl-information-transfer.cjs');
const obl_data_masking = require('./obl-data-masking.cjs');
const obl_data_leakage_prevention = require('./obl-data-leakage-prevention.cjs');
const obl_clear_desk_screen = require('./obl-clear-desk-screen.cjs');
const obl_privileged_utilities = require('./obl-privileged-utilities.cjs');
const obl_capacity_management = require('./obl-capacity-management.cjs');
const obl_outsourced_development = require('./obl-outsourced-development.cjs');
const obl_environment_separation = require('./obl-environment-separation.cjs');
const obl_audit_testing_protection = require('./obl-audit-testing-protection.cjs');
const obl_special_interest_groups = require('./obl-special-interest-groups.cjs');
const obl_project_security = require('./obl-project-security.cjs');
const obl_environmental_threats = require('./obl-environmental-threats.cjs');
const obl_secure_area_working = require('./obl-secure-area-working.cjs');
const obl_equipment_siting = require('./obl-equipment-siting.cjs');
const obl_offsite_asset_security = require('./obl-offsite-asset-security.cjs');
const obl_supporting_utilities = require('./obl-supporting-utilities.cjs');
const obl_cabling_security = require('./obl-cabling-security.cjs');
const obl_equipment_maintenance = require('./obl-equipment-maintenance.cjs');
const obl_endpoint_device_security = require('./obl-endpoint-device-security.cjs');
const obl_source_code_access = require('./obl-source-code-access.cjs');
const obl_test_information_protection = require('./obl-test-information-protection.cjs');
const obl_processing_instruction_compliance = require('./obl-processing-instruction-compliance.cjs');
const obl_dpa_instruction_conflict_notification = require('./obl-dpa-instruction-conflict-notification.cjs');
const obl_retention_deletion_per_dpa = require('./obl-retention-deletion-per-dpa.cjs');
const obl_data_return_or_erasure_on_exit = require('./obl-data-return-or-erasure-on-exit.cjs');
const obl_approved_processing_locations = require('./obl-approved-processing-locations.cjs');
const obl_subprocessor_registry = require('./obl-subprocessor-registry.cjs');
const obl_subprocessor_approval = require('./obl-subprocessor-approval.cjs');
const obl_subprocessor_oversight = require('./obl-subprocessor-oversight.cjs');
const obl_transfer_basis_documentation = require('./obl-transfer-basis-documentation.cjs');
const obl_processor_dsr_assistance = require('./obl-processor-dsr-assistance.cjs');
const obl_controller_breach_notification = require('./obl-controller-breach-notification.cjs');
const obl_dpa_breach_assistance = require('./obl-dpa-breach-assistance.cjs');
const obl_ai_policy = require('./obl-ai-policy.cjs');
const obl_ai_roles_and_responsibilities = require('./obl-ai-roles-and-responsibilities.cjs');
const obl_ai_resources_inventory = require('./obl-ai-resources-inventory.cjs');
const obl_ai_impact_assessment = require('./obl-ai-impact-assessment.cjs');
const obl_ai_responsible_design = require('./obl-ai-responsible-design.cjs');
const obl_ai_verification_deployment = require('./obl-ai-verification-deployment.cjs');
const obl_ai_operation_monitoring = require('./obl-ai-operation-monitoring.cjs');
const obl_ai_technical_documentation = require('./obl-ai-technical-documentation.cjs');
const obl_ai_data_management = require('./obl-ai-data-management.cjs');
const obl_ai_external_reporting = require('./obl-ai-external-reporting.cjs');
const obl_ai_responsible_use = require('./obl-ai-responsible-use.cjs');
const obl_ai_third_party_management = require('./obl-ai-third-party-management.cjs');
const obl_ai_prohibited_practices_screening = require('./obl-ai-prohibited-practices-screening.cjs');
const obl_ai_fundamental_rights_impact_assessment = require('./obl-ai-fundamental-rights-impact-assessment.cjs');
const obl_ai_conformity_assessment_ce = require('./obl-ai-conformity-assessment-ce.cjs');
const obl_ai_post_market_monitoring = require('./obl-ai-post-market-monitoring.cjs');
const obl_ai_gpai_model_duties = require('./obl-ai-gpai-model-duties.cjs');
const obl_ai_risk_management = require('./obl-ai-risk-management.cjs');
const obl_cra_manufacturer_essential_requirements = require('./obl-cra-manufacturer-essential-requirements.cjs');
const obl_cra_product_risk_assessment = require('./obl-cra-product-risk-assessment.cjs');
const obl_cra_sbom = require('./obl-cra-sbom.cjs');
const obl_cra_vulnerability_handling_process = require('./obl-cra-vulnerability-handling-process.cjs');
const obl_cra_support_period = require('./obl-cra-support-period.cjs');
const obl_cra_coordinated_vulnerability_disclosure = require('./obl-cra-coordinated-vulnerability-disclosure.cjs');
const obl_cra_secure_update_distribution = require('./obl-cra-secure-update-distribution.cjs');
const obl_cra_actively_exploited_vuln_report = require('./obl-cra-actively-exploited-vuln-report.cjs');
const obl_cra_severe_incident_report = require('./obl-cra-severe-incident-report.cjs');
const obl_cra_user_vuln_notification = require('./obl-cra-user-vuln-notification.cjs');
const obl_cra_conformity_assessment_ce = require('./obl-cra-conformity-assessment-ce.cjs');
const obl_cra_technical_documentation = require('./obl-cra-technical-documentation.cjs');
const obl_cra_importer_due_diligence = require('./obl-cra-importer-due-diligence.cjs');
const obl_cra_distributor_due_diligence = require('./obl-cra-distributor-due-diligence.cjs');
const obl_ski_control_effectiveness_reporting = require('./obl-ski-control-effectiveness-reporting.cjs');
const obl_ski_policy_exception_management = require('./obl-ski-policy-exception-management.cjs');
const obl_ski_national_security_clearance = require('./obl-ski-national-security-clearance.cjs');
const obl_ski_incomplete_screening_access_restriction = require('./obl-ski-incomplete-screening-access-restriction.cjs');
const obl_ski_nda_breach_and_termination_process = require('./obl-ski-nda-breach-and-termination-process.cjs');
const obl_ski_threat_specific_awareness_training = require('./obl-ski-threat-specific-awareness-training.cjs');
const obl_ski_operational_security_training = require('./obl-ski-operational-security-training.cjs');
const obl_ski_remote_security_training = require('./obl-ski-remote-security-training.cjs');
const obl_ski_termination_data_return_confirmation = require('./obl-ski-termination-data-return-confirmation.cjs');
const obl_ski_notice_period_data_restriction = require('./obl-ski-notice-period-data-restriction.cjs');
const obl_ski_endpoint_network_protection = require('./obl-ski-endpoint-network-protection.cjs');
const obl_ski_full_disk_encryption = require('./obl-ski-full-disk-encryption.cjs');
const obl_ski_media_inspection_rights = require('./obl-ski-media-inspection-rights.cjs');
const obl_ski_email_security_controls = require('./obl-ski-email-security-controls.cjs');
const obl_ski_data_protection_all_locations = require('./obl-ski-data-protection-all-locations.cjs');
const obl_ski_critical_data_technical_controls = require('./obl-ski-critical-data-technical-controls.cjs');
const obl_ski_privacy_breach_handling = require('./obl-ski-privacy-breach-handling.cjs');
const obl_ski_byod_policy = require('./obl-ski-byod-policy.cjs');
const obl_ski_byod_device_register = require('./obl-ski-byod-device-register.cjs');
const obl_ski_byod_device_configuration = require('./obl-ski-byod-device-configuration.cjs');
const obl_ski_advanced_dlp_monitoring = require('./obl-ski-advanced-dlp-monitoring.cjs');
const obl_ski_iam_procedures = require('./obl-ski-iam-procedures.cjs');
const obl_ski_identity_lifecycle = require('./obl-ski-identity-lifecycle.cjs');
const obl_ski_role_based_access_control = require('./obl-ski-role-based-access-control.cjs');
const obl_ski_threat_triggered_access_review = require('./obl-ski-threat-triggered-access-review.cjs');
const obl_ski_remote_access_controls = require('./obl-ski-remote-access-controls.cjs');
const obl_ski_sub_supplier_information_guidelines = require('./obl-ski-sub-supplier-information-guidelines.cjs');
const obl_ski_third_party_software_modification = require('./obl-ski-third-party-software-modification.cjs');
const obl_sdlc_risk_management = require('./obl-sdlc-risk-management.cjs');
const obl_sdlc_peer_review = require('./obl-sdlc-peer-review.cjs');
const obl_sdlc_regression_test = require('./obl-sdlc-regression-test.cjs');

// ── Obligation Catalog ───────────────────────────────────────────────────────

const OBLIGATION_CATALOG = [
  obl_policy_defined,
  obl_policy_review,
  obl_roles_responsibilities,
  obl_segregation_duties,
  obl_management_commitment,
  obl_management_body_accountability,
  obl_authority_contacts,
  obl_threat_intelligence_review,
  obl_asset_inventory,
  obl_acceptable_use,
  obl_asset_return,
  obl_data_classification,
  obl_access_control_policy,
  obl_privileged_access_management,
  obl_access_rights_review,
  obl_vendor_policy,
  obl_vendor_assessment,
  obl_vendor_contracts,
  obl_physical_security,
  obl_physical_monitoring,
  obl_incident_response_plan,
  obl_incident_response_exercise,
  obl_incident_evidence,
  obl_incident_learning,
  obl_regulatory_incident_notification,
  obl_incident_communication,
  obl_bcp_plan,
  obl_business_impact_analysis,
  obl_bc_exercise,
  obl_backup_test,
  obl_legal_register,
  obl_compliance_review,
  obl_independent_review,
  obl_security_event_reporting,
  obl_employee_screening,
  obl_employment_terms,
  obl_security_awareness,
  obl_training_deadline_compliance,
  obl_disciplinary_process,
  obl_nda_signed,
  obl_offboarding_access,
  obl_risk_assessment,
  obl_risk_treatment,
  obl_security_objectives,
  obl_isms_context_scope,
  obl_isms_resources,
  obl_isms_communication_awareness,
  obl_operating_procedures,
  obl_operational_planning,
  obl_continual_improvement,
  obl_management_review,
  obl_isms_metrics,
  obl_internal_audit,
  obl_vulnerability_scan,
  obl_patch_management,
  obl_software_installation,
  obl_infrastructure_redundancy,
  obl_security_monitoring,
  obl_logging_review,
  obl_network_security_review,
  obl_sdlc_policy,
  obl_security_testing,
  obl_pentest_external,
  obl_change_management,
  obl_cryptography_policy,
  obl_records_protection,
  obl_data_deletion,
  obl_secure_authentication,
  obl_remote_working_policy,
  obl_privacy_pii,
  obl_lawful_basis,
  obl_consent,
  obl_data_subject_rights,
  obl_data_portability,
  obl_transparency_notices,
  obl_records_of_processing,
  obl_dpia,
  obl_dpo,
  obl_international_transfer,
  obl_privacy_by_design,
  obl_malware_protection,
  obl_email_security,
  obl_config_management,
  obl_web_filtering,
  obl_payment_application_security,
  obl_storage_media_management,
  obl_vulnerability_scan_programme,
  obl_cde_scoping,
  obl_poi_tamper_protection,
  obl_ip_rights,
  obl_equipment_disposal,
  obl_information_transfer,
  obl_data_masking,
  obl_data_leakage_prevention,
  obl_clear_desk_screen,
  obl_privileged_utilities,
  obl_capacity_management,
  obl_outsourced_development,
  obl_environment_separation,
  obl_audit_testing_protection,
  obl_special_interest_groups,
  obl_project_security,
  obl_environmental_threats,
  obl_secure_area_working,
  obl_equipment_siting,
  obl_offsite_asset_security,
  obl_supporting_utilities,
  obl_cabling_security,
  obl_equipment_maintenance,
  obl_endpoint_device_security,
  obl_source_code_access,
  obl_test_information_protection,
  obl_processing_instruction_compliance,
  obl_dpa_instruction_conflict_notification,
  obl_retention_deletion_per_dpa,
  obl_data_return_or_erasure_on_exit,
  obl_approved_processing_locations,
  obl_subprocessor_registry,
  obl_subprocessor_approval,
  obl_subprocessor_oversight,
  obl_transfer_basis_documentation,
  obl_processor_dsr_assistance,
  obl_controller_breach_notification,
  obl_dpa_breach_assistance,
  obl_ai_policy,
  obl_ai_roles_and_responsibilities,
  obl_ai_resources_inventory,
  obl_ai_impact_assessment,
  obl_ai_responsible_design,
  obl_ai_verification_deployment,
  obl_ai_operation_monitoring,
  obl_ai_technical_documentation,
  obl_ai_data_management,
  obl_ai_external_reporting,
  obl_ai_responsible_use,
  obl_ai_third_party_management,
  obl_ai_prohibited_practices_screening,
  obl_ai_fundamental_rights_impact_assessment,
  obl_ai_conformity_assessment_ce,
  obl_ai_post_market_monitoring,
  obl_ai_gpai_model_duties,
  obl_ai_risk_management,
  obl_cra_manufacturer_essential_requirements,
  obl_cra_product_risk_assessment,
  obl_cra_sbom,
  obl_cra_vulnerability_handling_process,
  obl_cra_support_period,
  obl_cra_coordinated_vulnerability_disclosure,
  obl_cra_secure_update_distribution,
  obl_cra_actively_exploited_vuln_report,
  obl_cra_severe_incident_report,
  obl_cra_user_vuln_notification,
  obl_cra_conformity_assessment_ce,
  obl_cra_technical_documentation,
  obl_cra_importer_due_diligence,
  obl_cra_distributor_due_diligence,
  obl_ski_control_effectiveness_reporting,
  obl_ski_policy_exception_management,
  obl_ski_national_security_clearance,
  obl_ski_incomplete_screening_access_restriction,
  obl_ski_nda_breach_and_termination_process,
  obl_ski_threat_specific_awareness_training,
  obl_ski_operational_security_training,
  obl_ski_remote_security_training,
  obl_ski_termination_data_return_confirmation,
  obl_ski_notice_period_data_restriction,
  obl_ski_endpoint_network_protection,
  obl_ski_full_disk_encryption,
  obl_ski_media_inspection_rights,
  obl_ski_email_security_controls,
  obl_ski_data_protection_all_locations,
  obl_ski_critical_data_technical_controls,
  obl_ski_privacy_breach_handling,
  obl_ski_byod_policy,
  obl_ski_byod_device_register,
  obl_ski_byod_device_configuration,
  obl_ski_advanced_dlp_monitoring,
  obl_ski_iam_procedures,
  obl_ski_identity_lifecycle,
  obl_ski_role_based_access_control,
  obl_ski_threat_triggered_access_review,
  obl_ski_remote_access_controls,
  obl_ski_sub_supplier_information_guidelines,
  obl_ski_third_party_software_modification,
  obl_sdlc_risk_management,
  obl_sdlc_peer_review,
  obl_sdlc_regression_test,
];

// ── Derived fields (frameworkIds + coversExternalIds) ───────────────────────
//
// For every obligation, derive the set of frameworks whose controls it helps
// satisfy, and the external control refs (e.g. `A.8.7`, `CIS-10.1`) it covers.
// Derivation is purely mechanical — based on the obligation's `scfIds` and the
// TCF → framework mapping index. Never hand-edit these fields on individual
// obligations; change `scfIds` instead.
(function deriveFrameworkFields() {
  const { controlToFrameworks, externalRefs } = buildControlIndex();
  for (const obligation of OBLIGATION_CATALOG) {
    const frameworkSet = new Set();
    const externalIdSet = new Set();
    for (const scfId of obligation.scfIds || []) {
      const fws = controlToFrameworks.get(scfId) || [];
      for (const fw of fws) frameworkSet.add(fw);
      const refs = externalRefs.get(scfId) || [];
      for (const r of refs) externalIdSet.add(r.externalId);
    }
    obligation.frameworkIds = [...frameworkSet];
    obligation.coversExternalIds = [...externalIdSet];
  }
})();

// ── Lookup helpers ──────────────────────────────────────────────────────────

/** Obligation definitions indexed by id for O(1) lookup */
const OBLIGATIONS_BY_ID = new Map(OBLIGATION_CATALOG.map((o) => [o.id, o]));

module.exports = {
  OBLIGATION_CATALOG,
  OBLIGATION_CATEGORIES,
  OBLIGATIONS_BY_ID,
  CADENCE_OPTIONS,
  cadenceToMs,
};
